// WebSocket Hook for Real-time Alumni Updates
// Falls back to HTTP polling when WebSocket is not available (e.g., AWS App Runner)
import { useEffect, useRef, useState, useCallback } from 'react';

export const useWebSocket = (userId) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const pollingInterval = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3; // Reduced to fail faster and switch to polling
  const reconnectDelay = 2000;
  const [usePolling, setUsePolling] = useState(false);
  const lastPollTime = useRef(Date.now());
  const POLLING_INTERVAL = 15000; // Poll every 15 seconds

  // HTTP Polling fallback
  const startPolling = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !userId) {
      console.log('Polling: Missing token or userId');
      return;
    }

    console.log('Polling: Starting HTTP polling mode');
    setUsePolling(true);
    setIsConnected(true); // Show as "Live" when polling is active
    setConnectionError(null);

    const poll = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const response = await fetch(`${backendUrl}/api/alumni/notifications/poll?since=${lastPollTime.current}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Poll failed: ${response.status}`);
        }

        const data = await response.json();
        lastPollTime.current = Date.now();

        // Process any new notifications/messages
        if (data.notifications && data.notifications.length > 0) {
          data.notifications.forEach(notification => {
            setLastMessage(notification);
          });
        }

        if (data.messages && data.messages.length > 0) {
          data.messages.forEach(message => {
            setLastMessage({ type: 'new_message', ...message });
          });
        }

        if (data.connection_requests && data.connection_requests.length > 0) {
          setLastMessage({ type: 'connection_request_update' });
        }

      } catch (error) {
        console.error('Polling: Error', error);
        // Don't set connectionError for polling failures - just skip this poll
      }
    };

    // Initial poll
    poll();

    // Set up polling interval
    pollingInterval.current = setInterval(poll, POLLING_INTERVAL);
  }, [userId, POLLING_INTERVAL]);

  const stopPolling = useCallback(() => {
    console.log('Polling: Stopping');
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    setUsePolling(false);
  }, []);

  const connect = useCallback(() => {
    // Don't connect if no userId or token
    const token = localStorage.getItem('token');
    if (!token || !userId) {
      console.log('WebSocket: Missing token or userId, skipping connection');
      return;
    }
    
    // Don't reconnect if already connected
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket: Already connected');
      return;
    }
    
    // Determine WebSocket URL based on environment
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, '').replace(/\/$/, '')
      : '//localhost:8000';
    const wsUrl = `${protocol}${host}/ws/alumni/${userId}?token=${token}`;

    console.log('WebSocket: Environment check', {
      protocol,
      rawBackendUrl: process.env.REACT_APP_BACKEND_URL,
      host,
      userId,
      hasToken: !!token,
      fullWsUrl: wsUrl.replace(/token=[^&]+/, 'token=REDACTED')
    });
    
    try {
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        console.log('WebSocket: Connected successfully');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        
        // Setup ping interval to keep connection alive
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };
      
      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket: Message received', data.type);
          
          // Don't set lastMessage for pong messages
          if (data.type !== 'pong') {
            setLastMessage(data);
          }
        } catch (error) {
          console.error('WebSocket: Error parsing message', error);
        }
      };
      
      ws.current.onerror = (error) => {
        console.error('WebSocket: Connection error', {
          error,
          readyState: ws.current?.readyState,
          url: wsUrl.replace(/token=[^&]+/, 'token=REDACTED'),
          timestamp: new Date().toISOString()
        });
        setConnectionError('Connection error occurred');
      };
      
      ws.current.onclose = (event) => {
        console.log('WebSocket: Connection closed', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString()
        });
        setIsConnected(false);
        
        // Clear ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }
        
        // Attempt to reconnect if not a deliberate close
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = reconnectDelay * Math.pow(1.5, reconnectAttempts.current - 1);
          console.log(`WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);

          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.log('WebSocket: Max reconnect attempts reached, switching to HTTP polling');
          setConnectionError(null); // Clear error since we're falling back to polling
          startPolling();
        }
      };
    } catch (error) {
      console.error('WebSocket: Failed to create connection', error);
      setConnectionError('Failed to create WebSocket connection');
      // Fall back to polling on connection failure
      startPolling();
    }
  }, [userId, startPolling]);
  
  const disconnect = useCallback(() => {
    console.log('WebSocket: Disconnecting');

    // Clear timeouts and intervals
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }

    // Stop polling if active
    stopPolling();

    // Close WebSocket connection
    if (ws.current) {
      ws.current.close(1000, 'User disconnect');
      ws.current = null;
    }

    setIsConnected(false);
  }, [stopPolling]);
  
  const sendMessage = useCallback((message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      return true;
    } else {
      console.warn('WebSocket: Cannot send message, not connected');
      return false;
    }
  }, []);
  
  // Setup and cleanup
  useEffect(() => {
    if (userId) {
      connect();
    }
    
    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [userId, connect, disconnect]);
  
  // Reconnect on visibility change (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userId && !isConnected) {
        console.log('WebSocket: Page visible, attempting reconnect');
        reconnectAttempts.current = 0; // Reset attempts on manual reconnect
        connect();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, isConnected, connect]);
  
  return {
    isConnected,
    lastMessage,
    connectionError,
    sendMessage,
    reconnect: () => {
      reconnectAttempts.current = 0;
      connect();
    }
  };
};

export default useWebSocket;